// ── Persistent Layout Shell ──

import { api } from './api.js';
import { renderSidebar, mountSidebar, updateSidebarContext, destroySidebar, refreshProjectList, getCachedProject } from './components/sidebar.js';
import {
  renderNavbar,
  renderTwoFaBanner,
  mountNavbar,
  updateNavbarBreadcrumb,
  updateNavbarSettingsLink,
  destroyNavbar,
  TWO_FA_BANNER_DISMISSED_KEY,
} from './components/navbar.js';
import { applyThemeForContext } from './ui.js';

// Keyed by String(projectId). Holds the whole project object (name + color_scheme),
// not just the name — C1265 needs color_scheme to paint the project's theme on switch.
const projectCache = new Map();

// Bumped on every updateLayoutContext() call; a stale async fetch that resolves after a
// newer navigation started must not overwrite the newer context (theme AND breadcrumb).
let _ctxSeq = 0;

// Browser tab / PWA window title. Mirrors the Electron main-process contract
// (getWindowTitle(), ai/todo/server/main.js, tt-electron-app.md C915) — project name first,
// U+2014 em dash space-padded, brand last — but keeps the web app's own brand string
// "TipΔTask Projects" (C1056/C1063) instead of the desktop's bare "TipΔTask". Same collapse
// guard as the desktop helper: a name equal to the brand never doubles it up.
const BASE_TITLE = 'TipΔTask Projects';

export function updateDocumentTitle(projectName) {
  const name = String(projectName || '').trim();
  document.title = (!name || name === BASE_TITLE) ? BASE_TITLE : `${name} — ${BASE_TITLE}`;
}

export function isLayoutMounted() {
  return !!document.getElementById('sidebar');
}

export function initLayout(user) {
  if (isLayoutMounted()) return;

  const app = document.getElementById('app');
  app.innerHTML = `
    <div class="app-layout" id="app-layout">
      ${renderSidebar()}
      <div class="app-main-wrapper">
        ${renderNavbar(user)}
        <main class="main" id="content">
          <div class="empty"><span class="spinner"></span></div>
        </main>
      </div>
    </div>
    <div class="sidebar-overlay" id="sidebar-overlay"></div>`;

  mountSidebar();
  mountNavbar();
  mountTwoFaBanner(user);
}

function isTwoFaBannerDismissed() {
  try {
    return localStorage.getItem(TWO_FA_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function dismissTwoFaBanner() {
  try {
    localStorage.setItem(TWO_FA_BANNER_DISMISSED_KEY, '1');
  } catch {
    // Non-critical preference; keep dismissal in-memory by removing the node.
  }
  document.getElementById('twofa-banner')?.remove();
}

function mountTwoFaBanner(user) {
  if (isTwoFaBannerDismissed()) return;

  const bannerHtml = renderTwoFaBanner(user);
  if (!bannerHtml) return;

  const content = document.getElementById('content');
  if (!content) return;

  content.insertAdjacentHTML('beforebegin', bannerHtml);
  document.getElementById('twofa-banner-dismiss')?.addEventListener('click', dismissTwoFaBanner);
}

export function getContentEl() {
  return document.getElementById('content');
}

export function teardownLayout() {
  destroySidebar();
  destroyNavbar();
  projectCache.clear();
  // login.js/invite.js call this and never call updateLayoutContext() afterward — reset the
  // title explicitly so a signed-out/invite screen doesn't keep showing the last project name.
  updateDocumentTitle(null);
}

// De-facto "current project changed" broadcast — every page calls this exactly once
// per render, with projectId null on the project-less routes (Dashboard, All Tasks,
// Security). Feeds ui.js's applyThemeForContext() so theme tracks project switches
// (C1265) alongside the existing sidebar/breadcrumb updates.
//
// `colorScheme` (C1303, optional) — pass the just-saved value right after a successful
// `PATCH /projects/:id { color_scheme }` (see settings.js's Appearance tab). projectCache
// has no exported invalidation path otherwise, so without this the entry this call (and
// getCachedProject()) would keep serving until a full page reload, repainting the OLD
// scheme on the next navigation.
export async function updateLayoutContext({ projectId, projectName, activeView, viewLabel, colorScheme: freshColorScheme }) {
  const seq = ++_ctxSeq;
  let name = projectName;
  let colorScheme = null;
  const hasProject = projectId !== null && projectId !== undefined && projectId !== '';

  if (hasProject) {
    const key = String(projectId);
    let cached = projectCache.get(key) || getCachedProject(projectId);

    if (freshColorScheme !== undefined) {
      // Caller already knows the new value — skip the fetch below and write it straight
      // onto the SAME object projectCache and sidebar's cachedProjects hold (both key off
      // this object reference, not a copy), so one assignment refreshes both caches.
      if (cached) cached.color_scheme = freshColorScheme;
      colorScheme = freshColorScheme;
    } else {
      if (!cached) {
        // Cache miss (first load, or a project sidebar's list hasn't fetched yet) — fetch
        // the full project so color_scheme is known, not just the name.
        try {
          const res = await api.get('/projects/' + projectId);
          // A newer navigation started while this awaited — its context already applied;
          // do not clobber it with this stale one (theme AND breadcrumb).
          if (seq !== _ctxSeq) return;
          cached = res.project;
        } catch {
          cached = null;
        }
      }
      colorScheme = cached?.color_scheme || null;
    }

    if (cached) projectCache.set(key, cached);
    name = name || cached?.name || 'Project';
  }

  applyThemeForContext({ projectId, colorScheme });
  updateSidebarContext(projectId, name, activeView);
  updateNavbarBreadcrumb(projectId, name, viewLabel);
  updateNavbarSettingsLink(projectId);
  updateDocumentTitle(hasProject ? name : null);
}

export { refreshProjectList };
